import { mount } from '@crown/framework/dom';
import type { ElNode, Instruction } from '@crown/framework/ir';
import type { BrandColor } from '../types';
import { brandColors } from '../types';
import template from './button.tsrx';

type StyleVariant = 'outline' | 'ghost';

export interface ButtonProps {
  variant?: BrandColor | StyleVariant | `${StyleVariant}-${BrandColor}`;
  size?: 'small' | 'medium' | 'large';
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  backgroundColor?: string;
  extraClasses?: string | string[];
}

const buttonSizeClasses = {
  small: 'btn-sm',
  medium: '',
  large: 'btn-lg',
};

const styleVariants: readonly StyleVariant[] = ['outline', 'ghost'];

function isStyleVariant(value: string): value is StyleVariant {
  return styleVariants.some((variant) => variant === value);
}

function isBrandColor(value: string): value is BrandColor {
  return brandColors.some((colour) => colour === value);
}

export function buttonClasses({
  variant = 'secondary',
  size = 'medium',
  extraClasses,
}: Pick<ButtonProps, 'variant' | 'size' | 'extraClasses'>): string {
  const classes = ['btn'];
  const sizeClass = buttonSizeClasses[size];
  if (sizeClass) classes.push(sizeClass);

  const parts = variant.split('-');
  let styleClass: StyleVariant | null = null;
  let colorClass: BrandColor | null = null;
  const [first, second] = parts;

  if (parts.length === 1 && first !== undefined) {
    if (isStyleVariant(first)) styleClass = first;
    else if (isBrandColor(first)) colorClass = first;
  } else if (
    parts.length === 2 &&
    first !== undefined &&
    second !== undefined
  ) {
    if (isStyleVariant(first) && isBrandColor(second)) {
      styleClass = first;
      colorClass = second;
    }
  }

  if (styleClass) classes.push(styleClass);
  if (colorClass) classes.push(colorClass);
  if (!styleClass && !colorClass) classes.push('secondary');

  if (extraClasses) {
    classes.push(
      ...(Array.isArray(extraClasses) ? extraClasses : [extraClasses]),
    );
  }
  return classes.join(' ');
}

export interface ButtonTemplateProps {
  readonly classes: string;
  readonly label: string;
  readonly disabled: boolean;
  readonly backgroundColor: string | null;
  readonly onClick: (() => void) | null;
}

const ButtonTemplate: (props: ButtonTemplateProps) => Instruction = template;

export function Button(props: ButtonProps): ElNode {
  const { label, disabled = false, onClick, backgroundColor } = props;
  const node = ButtonTemplate({
    classes: buttonClasses(props),
    label,
    disabled,
    backgroundColor: backgroundColor ?? null,
    onClick: onClick ?? null,
  });
  if (node.t !== 'el') throw new Error('Button renders one element');
  return node;
}

export const createButton = (props: ButtonProps): HTMLButtonElement => {
  const holder = document.createElement('div');
  mount(Button(props), holder);
  const button = holder.querySelector('button');
  if (!button) throw new Error('Button did not mount a button element');
  return button;
};
